import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, Loader2, GripVertical, Video, FileText, ChevronDown, ChevronUp } from "lucide-react";
import api, { apiErr } from "@/lib/api";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

const EMPTY = {
  slug: "", title: "", subtitle: "", description: "", level: "Beginner", category: "Trading",
  duration: "", instructor: "KRV Prime Faculty", language: "English", price: 0,
  status: "available", image: "", featured: false,
  outcomes: [], format: [], faqs: [], curriculum: [], bonus_event_id: "",
};

function Field({ label, value, onChange, type = "text", placeholder, testid, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-xs text-slate-500">{label}</label>
      <input type={type} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 h-10 rounded-md border border-border px-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
    </div>
  );
}

function LessonRow({ lesson, onChange, onRemove }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => onChange({ ...lesson, [k]: v });

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ ...lesson, video_type: "file", video_file: data.url, video_url: "" });
      toast.success("Video uploaded.");
    } catch (e) { toast.error(apiErr(e)); }
    setUploading(false);
  };

  return (
    <div className="border border-border rounded-md p-3 bg-white space-y-2" data-testid="lesson-row">
      <div className="flex items-center gap-2">
        <input value={lesson.title || ""} placeholder="Lesson title" onChange={(e) => set("title", e.target.value)} data-testid="lesson-title"
          className="flex-1 h-9 rounded-md border border-border px-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
        <input value={lesson.duration || ""} placeholder="e.g. 12:30" onChange={(e) => set("duration", e.target.value)}
          className="w-24 h-9 rounded-md border border-border px-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
        <label className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap">
          <input type="checkbox" checked={!!lesson.preview} onChange={(e) => set("preview", e.target.checked)} data-testid="lesson-preview" /> Free preview
        </label>
        <button onClick={onRemove} className="text-red-500 hover:text-red-700" data-testid="remove-lesson"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="flex items-center gap-2">
        <select value={lesson.video_type || "youtube"} onChange={(e) => set("video_type", e.target.value)} data-testid="lesson-video-type"
          className="h-9 rounded-md border border-border px-2 text-sm outline-none focus:ring-2 focus:ring-gold">
          <option value="youtube">YouTube</option>
          <option value="vimeo">Vimeo</option>
          <option value="file">Uploaded file</option>
        </select>
        {lesson.video_type === "file" ? (
          <div className="flex-1 flex items-center gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-navy text-white text-xs font-medium hover:bg-navy-light disabled:opacity-60" data-testid="lesson-upload-btn">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {uploading ? "Uploading…" : "Upload video"}
            </button>
            <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            {lesson.video_file && <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><Video className="h-3.5 w-3.5" /> Uploaded</span>}
          </div>
        ) : (
          <input value={lesson.video_url || ""} placeholder="Paste YouTube / Vimeo link" onChange={(e) => set("video_url", e.target.value)} data-testid="lesson-video-url"
            className="flex-1 h-9 rounded-md border border-border px-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
        )}
      </div>
      <textarea value={lesson.content || ""} placeholder="Lesson notes / description (optional)" rows={2} onChange={(e) => set("content", e.target.value)}
        className="w-full rounded-md border border-border px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
    </div>
  );
}

function ModuleCard({ mod, index, onChange, onRemove }) {
  const [open, setOpen] = useState(true);
  const setLesson = (li, l) => {
    const lessons = [...mod.lessons]; lessons[li] = l; onChange({ ...mod, lessons });
  };
  const addLesson = () => onChange({ ...mod, lessons: [...(mod.lessons || []), { id: uid(), title: "", duration: "", preview: false, video_type: "youtube", video_url: "", video_file: "", content: "" }] });
  const removeLesson = (li) => onChange({ ...mod, lessons: mod.lessons.filter((_, i) => i !== li) });

  return (
    <div className="border border-border rounded-lg bg-subtle" data-testid={`module-card-${index}`}>
      <div className="flex items-center gap-2 p-3">
        <GripVertical className="h-4 w-4 text-slate-300" />
        <span className="text-xs font-semibold text-slate-400">Module {index + 1}</span>
        <input value={mod.title || ""} placeholder="Module title" onChange={(e) => onChange({ ...mod, title: e.target.value })} data-testid="module-title"
          className="flex-1 h-9 rounded-md border border-border px-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
        <button onClick={() => setOpen((o) => !o)} className="text-slate-400 hover:text-navy">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
        <button onClick={onRemove} className="text-red-500 hover:text-red-700" data-testid="remove-module"><Trash2 className="h-4 w-4" /></button>
      </div>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {(mod.lessons || []).map((l, li) => (
            <LessonRow key={l.id || li} lesson={l} onChange={(nl) => setLesson(li, nl)} onRemove={() => removeLesson(li)} />
          ))}
          <button onClick={addLesson} className="inline-flex items-center gap-1.5 text-sm text-navy hover:text-gold transition-colors" data-testid="add-lesson">
            <Plus className="h-4 w-4" /> Add lesson
          </button>
        </div>
      )}
    </div>
  );
}

export function CourseBuilder() {
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState(null);
  const [recordedEvents, setRecordedEvents] = useState([]);
  const imgRef = useRef();

  const load = () => api.get("/courses").then((r) => setCourses(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => {
    load();
    api.get("/admin/events").then((r) => setRecordedEvents((r.data || []).filter((e) => e.event_type === "recorded"))).catch(() => {});
  }, []);

  const edit = async (slug) => {
    try {
      const { data } = await api.get(`/admin/courses/${slug}`);
      // course detail returns sanitized curriculum; fetch full via admin list fallback not available,
      // so we keep whatever is present. Ensure arrays.
      setForm({ ...EMPTY, ...data, outcomes: data.outcomes || [], format: data.format || [], faqs: data.faqs || [], curriculum: data.curriculum || [] });
      window.scrollTo(0, 0);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.slug || !form.title) { toast.error("Slug and title are required."); return; }
    const payload = {
      ...form,
      price: parseInt(form.price || 0, 10),
      outcomes: Array.isArray(form.outcomes) ? form.outcomes : String(form.outcomes || "").split("\n").map((s) => s.trim()).filter(Boolean),
      format: Array.isArray(form.format) ? form.format : String(form.format || "").split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      await api.post("/admin/courses", payload);
      toast.success("Course saved.");
      setForm(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (slug) => {
    if (!window.confirm("Delete this course?")) return;
    try { await api.delete(`/admin/courses/${slug}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", data.url); toast.success("Image uploaded.");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!form) {
    return (
      <div data-testid="course-builder">
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-slate-500">{courses.length} courses</p>
          <button onClick={() => setForm({ ...EMPTY })} data-testid="add-course-btn" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-navy text-white text-sm font-medium hover:bg-navy-light transition-colors">
            <Plus className="h-4 w-4" /> Add course
          </button>
        </div>
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-left text-xs text-slate-500"><tr>
              <th className="px-4 py-3 font-medium">Title</th><th className="px-4 py-3 font-medium">Level</th>
              <th className="px-4 py-3 font-medium">Price</th><th className="px-4 py-3 font-medium">Lessons</th>
              <th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {courses.map((c) => (
                <tr key={c.slug} data-testid={`course-row-${c.slug}`}>
                  <td className="px-4 py-3 text-slate-700">{c.title}</td>
                  <td className="px-4 py-3 text-slate-500">{c.level}</td>
                  <td className="px-4 py-3 text-slate-500">{c.price ? `₹${c.price}` : "Free"}</td>
                  <td className="px-4 py-3 text-slate-500">{c.modules_count || 0}</td>
                  <td className="px-4 py-3 text-slate-500">{c.status}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => edit(c.slug)} data-testid={`edit-${c.slug}`} className="text-navy hover:text-gold mr-3"><Pencil className="h-4 w-4 inline" /></button>
                    <button onClick={() => del(c.slug)} data-testid={`delete-${c.slug}`} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="course-form" className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-display text-xl font-bold text-navy">{form.id ? "Edit" : "New"} course</h3>
        <div className="flex gap-2">
          <button onClick={() => setForm(null)} className="h-9 px-4 rounded-md border border-border text-sm">Cancel</button>
          <button onClick={save} data-testid="save-course-btn" className="h-9 px-5 rounded-md bg-gold text-navy font-semibold text-sm hover:bg-gold-soft transition-colors">Save course</button>
        </div>
      </div>

      <div className="bg-subtle border border-border rounded-lg p-5 grid sm:grid-cols-2 gap-4">
        <Field label="Slug (URL id) *" value={form.slug} onChange={(v) => set("slug", v)} testid="course-slug" />
        <Field label="Title *" value={form.title} onChange={(v) => set("title", v)} testid="course-title-input" />
        <Field label="Subtitle" value={form.subtitle} onChange={(v) => set("subtitle", v)} full />
        <div className="sm:col-span-2">
          <label className="text-xs text-slate-500">Description</label>
          <textarea value={form.description || ""} rows={3} onChange={(e) => set("description", e.target.value)} className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
        </div>
        <Field label="Level" value={form.level} onChange={(v) => set("level", v)} placeholder="Beginner / Intermediate / Advanced" />
        <Field label="Category" value={form.category} onChange={(v) => set("category", v)} />
        <Field label="Duration" value={form.duration} onChange={(v) => set("duration", v)} placeholder="e.g. 6 weeks" />
        <Field label="Instructor" value={form.instructor} onChange={(v) => set("instructor", v)} />
        <Field label="Language" value={form.language} onChange={(v) => set("language", v)} />
        <div>
          <label className="text-xs text-slate-500">Price (₹) — 0 = Free</label>
          <input type="number" value={form.price ?? 0} onChange={(e) => set("price", e.target.value)} data-testid="course-price" className="w-full mt-1 h-10 rounded-md border border-border px-3 text-sm outline-none focus:ring-2 focus:ring-gold" />
        </div>
        <div>
          <label className="text-xs text-slate-500">Status</label>
          <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="course-status" className="w-full mt-1 h-10 rounded-md border border-border px-3 text-sm outline-none focus:ring-2 focus:ring-gold">
            <option value="available">Available</option>
            <option value="coming_soon">Coming soon</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-slate-500">Bundled bonus recording (buyers get this recorded webinar free)</label>
          <select value={form.bonus_event_id || ""} onChange={(e) => set("bonus_event_id", e.target.value)} data-testid="course-bonus-event" className="w-full mt-1 h-10 rounded-md border border-border px-3 text-sm bg-white outline-none focus:ring-2 focus:ring-gold">
            <option value="">None</option>
            {recordedEvents.map((ev) => <option key={ev.id} value={ev.id}>{ev.title}{ev.price ? ` (₹${ev.price})` : " (Free)"}</option>)}
          </select>
          {recordedEvents.length === 0 && <p className="text-xs text-slate-400 mt-1">Create a Recorded event in the Events tab to bundle it here.</p>}
        </div>
        <div className="sm:col-span-2 flex items-end gap-3">
          <Field label="Cover image URL" value={form.image} onChange={(v) => set("image", v)} full />
          <button type="button" onClick={() => imgRef.current?.click()} className="h-10 px-3 rounded-md border border-border text-sm whitespace-nowrap inline-flex items-center gap-1.5"><Upload className="h-4 w-4" /> Upload</button>
          <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured on homepage</label>
        <div className="sm:col-span-2">
          <label className="text-xs text-slate-500">Learning outcomes (one per line)</label>
          <textarea value={(form.outcomes || []).join("\n")} rows={3} onChange={(e) => set("outcomes", e.target.value.split("\n"))} className="w-full mt-1 rounded-md border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
        </div>
      </div>

      {/* Curriculum */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-lg font-semibold text-navy">Curriculum (modules & lessons)</h4>
          <button onClick={() => set("curriculum", [...(form.curriculum || []), { title: "", lessons: [] }])} data-testid="add-module" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-navy text-white text-sm font-medium hover:bg-navy-light transition-colors">
            <Plus className="h-4 w-4" /> Add module
          </button>
        </div>
        <div className="space-y-3">
          {(form.curriculum || []).map((m, mi) => (
            <ModuleCard key={mi} mod={m} index={mi}
              onChange={(nm) => { const c = [...form.curriculum]; c[mi] = nm; set("curriculum", c); }}
              onRemove={() => set("curriculum", form.curriculum.filter((_, i) => i !== mi))} />
          ))}
          {(!form.curriculum || form.curriculum.length === 0) && <p className="text-sm text-slate-400">No modules yet. Add a module to start building lessons.</p>}
        </div>
      </div>

      {/* FAQs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-lg font-semibold text-navy">FAQs</h4>
          <button onClick={() => set("faqs", [...(form.faqs || []), { q: "", a: "" }])} data-testid="add-faq" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-navy text-white text-sm font-medium hover:bg-navy-light transition-colors">
            <Plus className="h-4 w-4" /> Add FAQ
          </button>
        </div>
        <div className="space-y-3">
          {(form.faqs || []).map((f, fi) => (
            <div key={fi} className="border border-border rounded-md p-3 bg-white space-y-2" data-testid={`faq-row-${fi}`}>
              <div className="flex items-center gap-2">
                <input value={f.q || ""} placeholder="Question" onChange={(e) => { const x = [...form.faqs]; x[fi] = { ...f, q: e.target.value }; set("faqs", x); }} className="flex-1 h-9 rounded-md border border-border px-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
                <button onClick={() => set("faqs", form.faqs.filter((_, i) => i !== fi))} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
              </div>
              <textarea value={f.a || ""} placeholder="Answer" rows={2} onChange={(e) => { const x = [...form.faqs]; x[fi] = { ...f, a: e.target.value }; set("faqs", x); }} className="w-full rounded-md border border-border px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
